import { useCallback, useEffect, useRef } from "react";

export type TriggerKey = "inactivity" | "quickSwipeUp" | "timeOnPage" | "exitIntent" | "scrollDepth";
export type TriggerMode = "and" | "or";

type UseTriggerCoordinatorOptions = {
  triggers: Partial<Record<TriggerKey, boolean>>;
  isModalOpened: boolean;
  onAllTriggered: (key: TriggerKey) => void;
  mode?: TriggerMode;
};

type UseTriggerCoordinatorResult = {
  onTrigger: (key: TriggerKey) => void;
};

/**
 * Coordinates multiple modal triggers.
 *
 * - `mode="and"`: opens the modal only when ALL enabled triggers have fired.
 * - `mode="or"` (default): opens the modal as soon as ANY enabled trigger fires.
 *
 * Resets fired state whenever the modal opens or the set of enabled triggers changes.
 *
 * Adding a new trigger:
 *  1. Add its key to `TriggerKey`.
 *  2. Pass `{ myNewTrigger: isMyNewTriggerEnabled }` in the `triggers` map.
 *  3. Call `onTrigger("myNewTrigger")` from the new hook.
 *  4. In the new hook, prefer refs (`useRef`) for mutable trigger flags/timers
 *     used by event listeners so callbacks stay stable and duplicate fires are avoided.
 */
export function useTriggerCoordinator({
  triggers,
  isModalOpened,
  onAllTriggered,
  mode = "or",
}: UseTriggerCoordinatorOptions): UseTriggerCoordinatorResult {
  const firedRef = useRef<Set<TriggerKey>>(new Set());

  const getEnabledKeys = useCallback(
    (): Set<TriggerKey> =>
      new Set(
        (Object.entries(triggers) as [TriggerKey, boolean | undefined][]).flatMap(([key, enabled]) =>
          enabled ? [key] : []
        )
      ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [JSON.stringify(triggers)]
  );

  useEffect(() => {
    firedRef.current = new Set();
  }, [isModalOpened, JSON.stringify(triggers)]);

  const onTrigger = useCallback(
    (key: TriggerKey) => {
      const enabled = getEnabledKeys();

      if (enabled.size === 0 || !enabled.has(key)) {
        return;
      }

      if (mode === "or") {
        onAllTriggered(key);
        return;
      }

      firedRef.current.add(key);

      const allFired = [...enabled].every((k) => firedRef.current.has(k));
      if (allFired) {
        firedRef.current = new Set();
        onAllTriggered(key);
      }
    },
    [getEnabledKeys, mode, onAllTriggered]
  );

  return { onTrigger };
}
